// Mode of transport (MOT) for a customs document.
//
// The backend fills `motType` from the CEISA carrier record, so the value that
// reaches the UI is whatever that record held: "SEA"/"AIR" in the common case,
// the Indonesian "LAUT"/"UDARA" on some older filings, and empty on documents
// where the carrier section was never completed.

export type MotType = 'SEA' | 'AIR';

/**
 * Resolves a raw `motType` into SEA, AIR, or null when it says neither.
 *
 * Sea is tested first on purpose: "air" is the Indonesian word for water, so a
 * value like "ANGKUTAN AIR LAUT" must not be read as a flight. The same ordering
 * is used in the draft editor's InitializationService, and the two need to agree.
 *
 * Returning null rather than defaulting is the point — the old table rendered a
 * plane icon for every value that did not contain "SEA", which meant an empty
 * motType and every "LAUT" filing both claimed to have flown.
 */
export function resolveMot(raw: string | null | undefined): MotType | null {
  const value = (raw || '').toUpperCase();
  if (!value.trim()) return null;
  if (value.includes('SEA') || value.includes('LAUT')) return 'SEA';
  if (value.includes('AIR') || value.includes('UDARA')) return 'AIR';
  return null;
}
